<template>
  <div class="quiz">
    <h1 v-if="title" class="quiz-title">{{ title }}</h1>

    <div class="quiz-outer-container">
      <div v-show="userAnswered !== true" class="quiz-question-container">
        <sanitas-form
          :id="formId"
          class="quiz-form"
          method="POST"
          :action="actionPath"
        >
          <span
            v-for="(question, questionIndex) of questionsArray"
            :key="questionIndex"
            :class="{ 'is-hidden': currentQuestionIndex !== questionIndex }"
          >
            <label class="question-label">
              {{ question.question }}
            </label>

            <radio-group-wrapper
              :id="`quiz-question${questionIndex}`"
              class="quiz-selection"
              :name="question.question"
              :items="question.answers"
              @update="onSelection"
            />
            <div v-show="error" class="quiz-question-error" v-text="error" />
          </span>
        </sanitas-form>
      </div>

      <div v-show="userAnswered && !isPsychicBalance" class="result-container">
        <div class="quiz-result-text san-richtext" v-html="resultText"></div>
      </div>

      <div v-show="userAnswered && isPsychicBalance" class="result-container">
        <Chart
          :results="barChart.results"
          :legend="barChart.legend"
          class="quiz-bar-chart"
        />

        <div class="san-richtext" v-html="psychicText"></div>

        <dotcom-jumplink class="quiz-extra-button" :href="extraButton.href">
          {{ extraButton.text }}
        </dotcom-jumplink>
      </div>
    </div>

    <p v-show="userAnswered !== true" class="quiz-counter">
      {{
        `${$t('counterText')} ${currentQuestionIndex + 1} / ${
          questionsArray.length
        }`
      }}
    </p>

    <div v-show="userAnswered !== true" class="quiz-button-container">
      <sanitas-button
        class="quiz-previous-button"
        secondary
        :full-width-in-xs="true"
        :disabled="currentQuestionIndex === 0"
        @click="userDidPressPreviousQuestion"
      >
        {{ $t('backButtonTitle') }}
      </sanitas-button>

      <sanitas-button
        primary
        class="quiz-next-button"
        :full-width-in-xs="true"
        @click="userDidPressNextQuestion"
      >
        {{ $t('nextButtonTitle') }}
      </sanitas-button>
    </div>
  </div>
</template>

<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'

import { client } from '@/helpers/http'
import { trackForm } from '@/helpers/tracker'

import SanitasForm from '../forms/components/Form'
import RadioGroupWrapper from '../forms/components/RadioGroupWrapper'
import Chart from './components/Chart'
import DotcomJumplink from './components/DotcomJumplink'

export default {
  name: 'QuizModule',
  components: {
    Chart,
    SanitasForm,
    RadioGroupWrapper,
    DotcomJumplink,
    SanitasButton: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/SanitasButton'
      )
    },
  },
  props: {
    quizTitle: {
      type: String,
      default: null,
    },
    actionPath: {
      type: String,
      default: null,
    },
    questions: {
      type: [String, Array],
      default() {
        return []
      },
    },
    formId: {
      type: String,
      default() {
        return `quiz-${Math.random()
          .toString(36)
          .replace(/[^a-z]+/g, '')
          .substr(0, 5)}`
      },
    },
  },
  setup() {
    const language = window.Sanitas.language || 'de'
    const loadLabels = lang => import(`./i18n/${lang}.json`)
    provideI36n(language, { load: loadLabels })
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },
  data: () => ({
    currentQuestionIndex: 0,
    userAnswered: false,
    extraButton: {},
    psychicText: null,
    resultText: null,
    questionsArray: [],
    error: '',
    barChart: {},
    title: null,
    isPsychicBalance: Boolean,
  }),
  mounted() {
    this.questionsArray =
      typeof this.questions === 'string'
        ? JSON.parse(this.questions)
        : this.questions
    this.title = this.quizTitle
  },
  methods: {
    userDidPressPreviousQuestion: function () {
      const isInFirstQuestion = this.currentQuestionIndex === 0
      if (isInFirstQuestion === true) {
        return
      }
      this.resetErrors()

      this.currentQuestionIndex--
    },
    userDidPressNextQuestion: async function () {
      const isInLastQuestion =
        this.currentQuestionIndex === this.questionsArray.length - 1

      const questionId = `#quiz-question${this.currentQuestionIndex}`
      const currentAnswer = this.$el.querySelector(
        `${questionId} input:checked`,
      )?.value

      const formData = new FormData(this.$el.querySelector('form'))

      if (this.currentQuestionIndex + 1 === this.questions.length - 1) {
        const result = await client().post(this.actionPath, formData)
        this.formResponse(result.data)
        return
      }

      // we do not allow users to go forward without selecting an option
      if (!currentAnswer || currentAnswer.length === 0) {
        this.error = this.$t('errorMessage')
        return
      }

      if (isInLastQuestion === true) {
        const result = await client().post(this.actionPath, formData)
        this.formResponse(result.data)
        return
      }
      this.resetErrors()
      this.currentQuestionIndex++
    },
    resetErrors() {
      this.error = ''
    },
    formResponse: function (response) {
      trackForm(this.formId, 'ComFormSubmitted')
      // only psychisc balance contains evaluations
      this.isPsychicBalance = response.evaluationResults !== undefined

      if (this.isPsychicBalance) {
        this.title = response.title

        this.barChart.results = response.evaluationResults
        this.barChart.legend = response.legends

        /**
         * http://stackoverflow.com/questions/22199662/how-to-inject-html-into-a-template-with-polymer
         * http://stackoverflow.com/questions/31000446/applying-styles-to-elements-inserted-with-innerhtml-in-polymer
         * WARNING: potential XSS vulnerability, but as the response will always come
         * from sanitas aem owned content the problem is mitigated
         */
        this.psychicText = response.text

        this.extraButton = response.extraButton
      } else {
        /**
         * http://stackoverflow.com/questions/22199662/how-to-inject-html-into-a-template-with-polymer
         * http://stackoverflow.com/questions/31000446/applying-styles-to-elements-inserted-with-innerhtml-in-polymer
         * WARNING: potential XSS vulnerability, but as the response will always come
         * from sanitas aem owned content the problem is mitigated
         */
        this.resultText = response.text
      }

      this.userAnswered = true
    },
    onSelection() {
      this.resetErrors()
    },
  },
}
</script>

<style scoped>
.quiz {
  display: block;
  border-bottom: 1px solid var(--c-secondary-neutral-3);
}

.is-hidden {
  display: block;
  opacity: 0;
  visibility: hidden;
  height: 0;
}

.quiz-title {
  /* overwrite global h1 font family */
  font-family: 'Open Sans', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.2;
  color: var(--c-primary-neutral-1);
  margin-top: 0;
  margin-bottom: 25px;
}

.quiz-outer-container {
  margin-bottom: 20px;
  border: 1px solid var(--c-secondary-neutral-3);
  border-radius: var(--border-radius);
  padding: 15px 15px 10px 15px;
}

.quiz-question-container .question-label {
  font-weight: 700;
  font-size: 18px;
  line-height: 1.25;
  color: var(--c-primary-neutral-1);
  display: block;
  margin-bottom: 15px;
}

.quiz-question-container,
.result-container,
.quiz-counter,
.quiz-button-container {
  transition: all 1s;
}

.quiz-question-error {
  font-weight: 700;
  font-size: 13px;
  line-height: 1.35;
  margin-bottom: 5px;
  margin-top: -10px;
  color: var(--c-secondary-color-1);
}

.result-container {
  img {
    width: 100%;
  }
}

.quiz-selection {
  font-size: 15px;
}

.quiz-counter {
  font-size: 13px;
  line-height: 1.35;
  color: var(--c-primary-neutral-1);

  display: block;
  text-align: right;
  margin-bottom: 20px;
}

.quiz-button-container {
  display: flex;
  flex-flow: column-reverse;
}

.quiz-next-button,
.quiz-previous-button {
  display: block;
}

.quiz-previous-button {
  margin-bottom: 30px;
}

.quiz-next-button {
  margin-bottom: 20px;
}

.quiz-bar-chart {
  margin-bottom: 60px;
}

.quiz-extra-button {
  margin-top: 30px;
}

@media (min-width: 543px) {
  .quiz-button-container {
    display: block;
    margin-bottom: 30px;
  }

  .quiz-next-button,
  .quiz-previous-button {
    display: inline-block;
    margin: 0 10px 0 0;
  }
}

@media (--md) {
  .quiz-title {
    margin-bottom: 40px;
  }

  .quiz-outer-container {
    padding: 25px 20px;
  }
}

@media (min-width: 992px) {
  .quiz-outer-container {
    padding: 30px 30px 20px 30px;
  }
}
</style>
